import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Plus, Trophy } from "lucide-react";

export default function Giveaways() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/giveaways").then(({ data }) => {
      setItems(data);
      setLoading(false);
    });
  }, []);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">All campaigns</div>
            <h1 className="font-display text-4xl font-bold tracking-tighter mt-2">Giveaways</h1>
          </div>
          <button
            onClick={() => navigate("/giveaways/new")}
            data-testid="create-giveaway-button"
            className="bg-zinc-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors flex items-center gap-2"
          >
            <Plus className="size-4" strokeWidth={1.5} /> New Giveaway
          </button>
        </div>

        {loading ? (
          <div className="text-zinc-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-zinc-200 rounded-md p-16 text-center bg-white">
            <Trophy className="size-10 text-zinc-300 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-display font-semibold text-xl">No giveaways yet</h3>
            <p className="text-sm text-zinc-500 mt-2">Launch your first sweepstakes campaign and start tracking entries.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((g) => (
              <Link
                key={g.giveaway_id}
                to={`/giveaways/${g.giveaway_id}`}
                data-testid={`giveaway-card-${g.giveaway_id}`}
                className="bg-white border border-zinc-200 rounded-md p-5 hover:shadow-sm hover:-translate-y-[1px] transition-all"
              >
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded ${
                    g.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-600"
                  }`}>
                    <span className={`size-1.5 rounded-full ${g.status === "active" ? "bg-emerald-500" : "bg-zinc-400"}`} />
                    {g.status}
                  </span>
                  <div className="text-xs text-zinc-500 tabular">${g.prize_value.toLocaleString()} prize</div>
                </div>
                <h3 className="font-display font-bold text-xl mt-3">{g.title}</h3>
                <p className="text-sm text-zinc-600 mt-2 line-clamp-2">{g.description}</p>
                <div className="border-t border-zinc-100 mt-5 pt-4 flex items-center justify-between text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Entries</div>
                    <div className="font-display font-bold text-lg tabular">{g.total_entries}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Entrants</div>
                    <div className="font-display font-bold text-lg tabular">{g.unique_entrants}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
